'use client';

import React from 'react';
import { Dialog, DialogHeader, DialogBody, DialogFooter, Button } from '@repo/ui';

export interface ConfirmDialogProps {
  isOpen: boolean;
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  variant?: 'default' | 'destructive';
  size?: 'sm' | 'md' | 'lg';
}

/**
 * A reusable confirmation dialog component with dynamic context and action handling.
 *
 * @example
 * ```tsx
 * <ConfirmDialog
 *   isOpen={isOpen}
 *   message="Please confirm the appeal was denied. This action cannot be reversed."
 *   onConfirm={handleConfirm}
 *   onCancel={handleCancel}
 * />
 * ```
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title = 'Confirm Action',
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  isLoading = false,
  variant = 'default',
  size = 'md',
}) => {
  const handleConfirm = () => {
    if (!isLoading) {
      onConfirm();
    }
  };

  const handleCancel = () => {
    if (!isLoading) {
      onCancel();
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleCancel}
      size={size}
      closeOnBackdrop={!isLoading}
      closeOnEscape={!isLoading}
      showCloseButton={!isLoading}
      ariaLabel={title}
    >
      <DialogHeader className="!px-0">
        <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-white">{title}</h3>
      </DialogHeader>
      <DialogBody className="!px-0">
        <p className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-300">{message}</p>
      </DialogBody>
      <DialogFooter className="!px-0 flex justify-between">
        <Button variant="outline" onClick={handleCancel} disabled={isLoading} size="md">
          {cancelText}
        </Button>
        <Button
          variant={variant === 'destructive' ? 'destructive' : 'primary'}
          onClick={handleConfirm}
          loading={isLoading}
          size="md"
        >
          {confirmText}
        </Button>
      </DialogFooter>
    </Dialog>
  );
};

export default ConfirmDialog;
